import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, Outlet, useLocation, Link } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import authService from "../../services/authService";
import menuService from "../../services/menuService";
import modulosSistemaService from "../../services/modulosSistemaService";
import { setModulosSistema, clearModulosSistema } from "../../utils/modulosSistema";
import notificacionesService from "../../services/notificacionesService";
import centrosService from "../../services/centrosService";
import "../../styles/dashboardTheme.css";
import { initSocket } from "../../utils/socketClient";
import { showToast } from "../../utils/showToast";
import { httpClient } from "../../services/httpClient";

// Al abrir el panel se disparan varias peticiones (menu, modulos, notificaciones,
// branding, socket) casi al mismo tiempo. Contra Render eso no es problema, pero
// contra el enlace de internet de la oficina una ráfaga de tantas conexiones nuevas
// a la vez puede hacer que alguna se pierda. Escalonarlas unos cientos de ms reduce
// esa ráfaga sin que el usuario note demora real.
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth >= 992;
  });
  const [menuPermisos, setMenuPermisos] = useState({});
  const [openModules, setOpenModules] = useState({});
  const [notificaciones, setNotificaciones] = useState([]);
  const [panelNotificacionesAbierto, setPanelNotificacionesAbierto] = useState(false);
  const [branding, setBranding] = useState(null);
  const getSubmodulosEntries = useCallback(
    (submodulos) => Object.entries(submodulos || {}).filter(([key]) => key !== "__meta"),
    []
  );
  const normalizarNotificacion = useCallback((n) => {
    if (!n) return null;
    let meta = null;
    if (typeof n.metadata === "object") meta = n.metadata;
    else {
      try { meta = n.metadata ? JSON.parse(n.metadata) : null; } catch (e) { meta = null; }
    }
    return {
      ...n,
      id: Number(n.id),
      leido: !!n.leido,
      metadata: meta,
    };
  }, []);
  const noLeidas = useMemo(
    () => (Array.isArray(notificaciones) ? notificaciones.filter((n) => !n.leido).length : 0),
    [notificaciones]
  );
  const activeNavRoute = useMemo(() => {
    let bestMatch = "";
    if (!menuPermisos) return null;

    Object.values(menuPermisos).forEach((submodulos) => {
      const submodulosEntries = getSubmodulosEntries(submodulos);
      submodulosEntries.forEach(([, permisos]) => {
        if (!permisos || permisos.length === 0) return;
        const permisoPrincipal =
          permisos.find((p) => p.ruta && !p.ruta.includes("/:id")) || permisos[0];
        if (!permisoPrincipal?.ruta) return;
        const ruta = `/dashboard${permisoPrincipal.ruta}`;
        if (location.pathname.startsWith(ruta) && ruta.length > bestMatch.length) {
          bestMatch = ruta;
        }
      });
    });

    return bestMatch || null;
  }, [menuPermisos, location.pathname, getSubmodulosEntries]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const rawUser = localStorage.getItem("user");

    if (!token || !rawUser) {
      navigate("/");
      return;
    }

    try {
      setUser(JSON.parse(rawUser));
    } catch {
      navigate("/");
    }
  }, [navigate]);

  useEffect(() => {
    const cargarMenu = async () => {
      try {
        const data = await menuService.obtener();
        setMenuPermisos(data);
      } catch (error) {
        console.error("Error cargando menu:", error);
      }
    };

    const cargarModulos = async () => {
      try {
        const data = await modulosSistemaService.listar();
        setModulosSistema(data);
      } catch (error) {
        console.error("Error cargando módulos del sistema:", error);
      }
    };

    let cancelled = false;
    cargarMenu();
    wait(200).then(() => {
      if (!cancelled) cargarModulos();
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const cargarNotificaciones = useCallback(async () => {
    try {
      const lista = await notificacionesService.listar();
      const parsed = Array.isArray(lista) ? lista.map(normalizarNotificacion).filter(Boolean) : [];
      setNotificaciones(parsed);
    } catch (error) {
      console.error("Error cargando notificaciones:", error);
    }
  }, [normalizarNotificacion]);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    wait(400).then(() => {
      if (!cancelled) cargarNotificaciones();
    });
    return () => {
      cancelled = true;
    };
  }, [cargarNotificaciones, user]);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    let checking = false;
    const intervalId = setInterval(async () => {
      if (cancelled || checking) return;
      const token = localStorage.getItem("token");
      if (!token) return;
      checking = true;
      try {
        await authService.me();
      } catch (error) {
        // httpClient maneja el 401 y fuerza logout
      } finally {
        checking = false;
      }
    }, 10000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    const socket = initSocket(token);
    if (!socket) return;

    const onNueva = (payload) => {
      const parsed = normalizarNotificacion(payload);
      if (!parsed) return;
      setNotificaciones((prev) => (prev.some((n) => n.id === parsed.id) ? prev : [parsed, ...prev]));
      showToast({
        type: parsed.tipo === "error" ? "danger" : "info",
        message: `${parsed.titulo || "Notificación"}: ${parsed.mensaje || ""}`,
        delay: 5000,
      });
    };

    const onActualizada = ({ id, leido, leido_en }) => {
      setNotificaciones((prev) =>
        prev.map((n) =>
          n.id === Number(id) ? { ...n, leido: !!leido, leido_en: leido_en || n.leido_en } : n
        )
      );
    };

    const onTodasLeidas = () => {
      setNotificaciones((prev) =>
        prev.map((n) => ({ ...n, leido: true, leido_en: n.leido_en || new Date().toISOString() }))
      );
    };

    const onEliminada = ({ id }) => {
      setNotificaciones((prev) => prev.filter((n) => n.id !== Number(id)));
    };

    socket.on("notificacion:nueva", onNueva);
    socket.on("notificacion:actualizada", onActualizada);
    socket.on("notificacion:todas-leidas", onTodasLeidas);
    socket.on("notificacion:eliminada", onEliminada);

    return () => {
      socket.off("notificacion:nueva", onNueva);
      socket.off("notificacion:actualizada", onActualizada);
      socket.off("notificacion:todas-leidas", onTodasLeidas);
      socket.off("notificacion:eliminada", onEliminada);
    };
  }, [user, normalizarNotificacion]);

  useEffect(() => {
    if (!menuPermisos) return;
    // Abrir solo el módulo que coincide con la ruta actual y cerrar el resto
    setOpenModules(() => {
      const next = {};
      Object.keys(menuPermisos).forEach((modulo) => {
        next[modulo] = false;
      });

      const activeModulo =
        Object.entries(menuPermisos).find(([modulo, submodulos]) =>
          getSubmodulosEntries(submodulos).some(([, permisos]) =>
            (permisos || []).some((p) => {
              const ruta = p?.ruta ? `/dashboard${p.ruta}` : "";
              return ruta && location.pathname.startsWith(ruta);
            })
          )
        )?.[0] || null;

      if (activeModulo) next[activeModulo] = true;
      return next;
    });
  }, [menuPermisos, location.pathname, getSubmodulosEntries]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleResize = () => {
      const isDesktop = window.innerWidth >= 992;
      if (isDesktop) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
        setSidebarCollapsed(false);
        setMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth < 992) {
      setSidebarOpen(false);
      setMenuOpen(false);
      setSidebarCollapsed(false);
    }
  }, [location.pathname]);

  const handleLogout = async () => {
    setMenuOpen(false);
    try {
      await authService.logout().catch(() => {});
    } catch (error) {
      console.error("Error solicitando cierre de sesion:", error);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      clearModulosSistema();
      navigate("/");
    }
  };

  const avatarLetter = user?.nombre_completo?.charAt(0)?.toUpperCase();
  const permisosCodigos = useMemo(
    () => (Array.isArray(user?.permisos) ? user.permisos.map((p) => p.codigo) : []),
    [user]
  );
  const canOpenConfig = permisosCodigos.includes("configuracion");

  const buildRemoteSrc = useCallback((path) => {
    if (!path) return "";
    const trimmed = String(path).trim();
    if (!trimmed) return "";
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    const baseUrl = httpClient?.baseUrl || "";
    if (!baseUrl) return trimmed;
    if (trimmed.startsWith("/")) return `${baseUrl}${trimmed}`;
    return `${baseUrl}/${trimmed}`;
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadBranding = async () => {
      try {
        const data = await centrosService.obtenerBrandingActual();
        if (!mounted) return;
        setBranding(data || null);
      } catch (error) {
        console.error("Error cargando branding:", error);
      }
    };
    if (user) {
      wait(600).then(() => {
        if (mounted) loadBranding();
      });
    }
    return () => {
      mounted = false;
    };
  }, [user]);

  const logoSrc = useMemo(
    () => buildRemoteSrc(branding?.logo_path_url || branding?.logo_url || branding?.logo_path),
    [branding?.logo_path_url, branding?.logo_url, branding?.logo_path, buildRemoteSrc]
  );
  const headerSrc = useMemo(
    () =>
      buildRemoteSrc(
        branding?.header_text_image_path_url ||
          branding?.header_text_image_url ||
          branding?.header_text_image_path
      ),
    [
      branding?.header_text_image_path_url,
      branding?.header_text_image_url,
      branding?.header_text_image_path,
      buildRemoteSrc
    ]
  );
  const brandLabel =
    branding?.alias || branding?.nombre || "ADEMY";
  const fallbackLogoSrc = `${process.env.PUBLIC_URL || ""}/AD2.png`;

  // El favicon en public/index.html queda fijo en AD2.png en tiempo de build;
  // aqui se actualiza en runtime al logo del centro. Se reaplica en cada
  // cambio de ruta (no solo cuando carga el branding) porque algunos
  // navegadores revierten el href mutado por JS al navegar via pushState.
  useEffect(() => {
    if (!logoSrc || typeof document === "undefined") return;
    document
      .querySelectorAll("link[rel~='icon'], link[rel='apple-touch-icon']")
      .forEach((link) => {
        link.href = logoSrc;
      });
  }, [logoSrc, location.pathname]);

  const toggleModule = (modulo) => {
    setOpenModules((prev) => {
      const currentlyOpen = !!prev[modulo];
      const next = {};
      Object.keys(menuPermisos).forEach((m) => {
        next[m] = false;
      });
      if (!currentlyOpen) next[modulo] = true;
      return next;
    });
  };

  const renderNav = () => {
    const modulos = Object.keys(menuPermisos);

    if (!modulos.length) {
      return <small className="text-white-50 px-3">Cargando menu...</small>;
    }

    const modulosOrdenados = modulos;

    const getModuloIcon = (submodulos) => {
      if (submodulos?.__meta?.icon) return submodulos.__meta.icon;
      for (const [key, permisos] of Object.entries(submodulos || {})) {
        if (key === "__meta") continue;
        const permisoConIcono = (permisos || []).find((p) => p?.icon);
        if (permisoConIcono?.icon) return permisoConIcono.icon;
      }
      return null;
    };

    return modulosOrdenados.map((modulo) => {
      const submodulos = menuPermisos[modulo];
      const submodulosEntries = getSubmodulosEntries(submodulos);

      if (!submodulosEntries.length) return null;

      const isOpen = !!openModules[modulo];
      const moduloBadge =
        modulo
          ?.split(/\s+/)
          .map((w) => w.charAt(0))
          .join("")
          .toUpperCase()
          .slice(0, 2) || modulo?.slice(0, 2)?.toUpperCase() || "?";
      const moduloIcon = getModuloIcon(submodulos);

      return (
        <div key={modulo} className="dashboard-nav-section">
          <button
            type="button"
            className="dashboard-nav-title d-flex align-items-center justify-content-between gap-2 w-100"
            onClick={() => toggleModule(modulo)}
            aria-expanded={isOpen}
            aria-controls={`submenu-${modulo}`}
            data-label={modulo}
            title={modulo}
            aria-label={modulo}
          >
            <span className="nav-title-badge" aria-hidden="true">
              {moduloIcon ? <i className={`${moduloIcon} fs-5`} /> : moduloBadge}
            </span>
            <span className="nav-title-text text-start">{modulo}</span>
            <i className={`bi ${isOpen ? "bi-chevron-up" : "bi-chevron-down"} nav-title-caret`} />
          </button>

          {isOpen && (
            <div id={`submenu-${modulo}`} className="dashboard-submenu">
              {submodulosEntries.map(([submodulo, permisos]) => {
                if (!permisos || permisos.length === 0) return null;

                const permisoPrincipal =
                  permisos.find((p) => p.ruta && !p.ruta.includes("/:id")) ||
                  permisos[0];

                const ruta = "/dashboard" + permisoPrincipal.ruta;
                const active = activeNavRoute === ruta;

                return (
                  <Link
                    key={permisoPrincipal.id || permisoPrincipal.ruta}
                    to={ruta}
                    className={`dashboard-nav-link ${active ? "active" : ""}`}
                    data-label={submodulo}
                    title={submodulo}
                    /* onClick={() => console.log(`Submodulo ${submodulo} ruta ${ruta}`)} */
                  >
                    <i className={`${permisoPrincipal.icon || "bi bi-circle"} fs-5`} />
                    <span className="dashboard-nav-text">{submodulo}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    });
  };

  const togglePanelNotificaciones = () => setPanelNotificacionesAbierto((prev) => !prev);

  const handleMarcarLeidaNoti = useCallback(async (id) => {
    try {
      await notificacionesService.marcarLeida(id);
      setNotificaciones((prev) =>
        prev.map((n) => (n.id === Number(id) ? { ...n, leido: true, leido_en: new Date().toISOString() } : n))
      );
    } catch (error) {
      console.error("No se pudo marcar como leída:", error);
    }
  }, []);

  const buildNotificacionLink = useCallback((n) => {
    if (!n) return "";
    const meta = n.metadata || {};
    const relatedType = String(n.relacionado_tipo || meta.relacionado_tipo || "").toLowerCase();
    const relatedId =
      n.relacionado_id ||
      meta.relacionado_id ||
      meta.bitacora_id ||
      meta.matricula_id ||
      meta.estudiante_id;

    const metaLink = typeof meta.enlace === "string" ? meta.enlace.trim() : "";
    if (!relatedType || !relatedId) return metaLink || "";

    if (relatedType === "bitacora") return `/dashboard/bitacora?bitacoraId=${relatedId}`;
    if (relatedType === "matricula") {
      const canSeeMatriculas =
        permisosCodigos.includes("matriculas.listar") ||
        permisosCodigos.includes("matriculas.crear") ||
        permisosCodigos.includes("matriculas.actualizar");
      const canSeePagos =
        permisosCodigos.includes("pagos.listar") || permisosCodigos.includes("pagos.crear");
      const titulo = typeof n.titulo === "string" ? n.titulo : "";
      const mensaje = typeof n.mensaje === "string" ? n.mensaje : "";
      const wantsPago =
        !!meta.tipo_pago || /pago|pagar|cobro/i.test(titulo) || /pago|pagar|cobro/i.test(mensaje);

      if (wantsPago && canSeePagos) {
        return `/dashboard/caja/pagos?matriculaId=${relatedId}`;
      }
      if (canSeeMatriculas) {
        return `/dashboard/matriculas?matriculaId=${relatedId}`;
      }
      if (canSeePagos) {
        return `/dashboard/caja/pagos?matriculaId=${relatedId}`;
      }
      return `/dashboard/matriculas?matriculaId=${relatedId}`;
    }
    if (relatedType === "estudiante") return `/dashboard/estudiantes?estudianteId=${relatedId}`;

    return metaLink || "";
  }, [permisosCodigos]);

  const isExternalLink = useCallback((link) => /^https?:\/\//i.test(link || ""), []);

  const handleNotificacionClick = useCallback(
    async (n) => {
      const link = buildNotificacionLink(n);
      if (!link) return;
      try {
        if (!n.leido) {
          await handleMarcarLeidaNoti(n.id);
        }
      } catch (error) {
        console.error("No se pudo marcar como leida:", error);
      }
      setPanelNotificacionesAbierto(false);
      if (isExternalLink(link)) {
        window.open(link, "_blank", "noopener,noreferrer");
      } else {
        navigate(link);
      }
    },
    [buildNotificacionLink, handleMarcarLeidaNoti, isExternalLink, navigate]
  );

  const handleMarcarTodasNoti = async () => {
    try {
      await notificacionesService.marcarTodas();
      setNotificaciones((prev) =>
        prev.map((n) => ({ ...n, leido: true, leido_en: n.leido_en || new Date().toISOString() }))
      );
    } catch (error) {
      console.error("No se pudieron marcar todas:", error);
    }
  };

  return (
    <div className="dashboard-shell">
      <aside
        className={`dashboard-sidebar ${sidebarOpen ? "open" : ""} ${
          sidebarCollapsed ? "collapsed" : ""
        }`}
      >
        <div className="dashboard-sidebar-body">
          <div className="dashboard-sidebar-top">
            <div className="dashboard-brand">
              {logoSrc ? (
                <img src={logoSrc} alt="Logo" className="dashboard-brand-logo" />
              ) : (
                <img src={fallbackLogoSrc} alt="Logo" className="dashboard-brand-logo" />
              )}
              {headerSrc ? (
                <img src={headerSrc} alt={brandLabel} className="dashboard-brand-header" />
              ) : (
                <span>{brandLabel}</span>
              )}
            </div>
            <button
              type="button"
              className="sidebar-collapse-btn d-none d-lg-inline-flex"
              aria-label={sidebarCollapsed ? "Expandir menu" : "Colapsar menu"}
              onClick={() => setSidebarCollapsed((prev) => !prev)}
            >
              <i
                className={`bi ${
                  sidebarCollapsed ? "bi-chevron-right" : "bi-chevron-left"
                }`}
              ></i>
            </button>
          </div>
          <nav className="dashboard-nav">{renderNav()}</nav>
        </div>
        <div className="dashboard-sidebar-footer">
          &copy; {new Date().getFullYear()} AlphaTechnologies
        </div>
      </aside>

      <div
        className={`dashboard-sidebar-overlay ${sidebarOpen ? "open" : ""}`}
        onClick={() => setSidebarOpen(false)}
      ></div>

      <div className="dashboard-main">
        <header className="dashboard-header">
          <div className="d-flex align-items-center gap-3">
            <button
              type="button"
              className="dashboard-sidebar-toggle d-lg-none"
              aria-label="Abrir menu"
              onClick={() => setSidebarOpen(true)}
            >
              <i className="bi bi-list"></i>
            </button>
            <span className="dashboard-header-title">Panel de Control</span>
          </div>

          <div className="dashboard-header-actions position-relative">
            <button
              type="button"
              className="btn btn-link text-muted p-0"
              aria-label="Notificaciones"
              onClick={togglePanelNotificaciones}
            >
              <i className="bi bi-bell fs-5 position-relative">
                {noLeidas > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                    {noLeidas}
                  </span>
                )}
              </i>
            </button>
            {panelNotificacionesAbierto && (
              <div
                className="card shadow-sm position-absolute end-0 mt-2 notification-panel"
                style={{
                  width: "min(400px, calc(100vw - 2rem))",
                  maxHeight: "420px",
                  zIndex: 20,
                  borderRadius: "16px",
                  overflow: "hidden",
                  border: "1px solid rgba(0,0,0,0.08)",
                  top: "calc(100% + 8px)",
                  right: window.innerWidth < 576 ? "-1rem" : "0"
                }}
              >
                <div className="card-header d-flex justify-content-between align-items-center bg-light">
                  <div>
                    <strong>Notificaciones</strong>
                    <div className="small text-muted">Alertas recientes</div>
                  </div>
                  <div className="d-flex gap-2 align-items-center">
                    <button
                      className="btn btn-sm btn-link text-decoration-none"
                      onClick={handleMarcarTodasNoti}
                      disabled={!notificaciones.length}
                      aria-label="Marcar todas como leídas"
                      title="Marcar todas como leídas"
                    >
                      <i className="bi bi-check2-all"></i>
                    </button>
                    <button
                      className="btn-close"
                      aria-label="Cerrar"
                      onClick={togglePanelNotificaciones}
                    ></button>
                  </div>
                </div>
                <ul className="list-group list-group-flush" style={{ overflowY: "auto", maxHeight: "360px" }}>
                  {Array.isArray(notificaciones) && notificaciones.length ? (
                    notificaciones.map((n) => (
                      <li
                        key={n.id}
                        className="list-group-item d-flex justify-content-between align-items-start"
                        style={{
                          borderLeft: n.leido ? "4px solid transparent" : "4px solid #0d6efd",
                          cursor: buildNotificacionLink(n) ? "pointer" : "default",
                        }}
                        onClick={() => handleNotificacionClick(n)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            handleNotificacionClick(n);
                          }
                        }}
                        role={buildNotificacionLink(n) ? "button" : "listitem"}
                        tabIndex={buildNotificacionLink(n) ? 0 : -1}
                      >
                        <div className="me-2">
                          <div className="fw-semibold">{n.titulo}</div>
                          <div className="small text-muted">{n.mensaje}</div>
                          {buildNotificacionLink(n) && (
                            <span className="small text-primary">Ver detalle</span>
                          )}
                        </div>
                        <div className="d-flex flex-column align-items-end gap-1">
                          {!n.leido && (
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleMarcarLeidaNoti(n.id);
                              }}
                              aria-label="Marcar como leída"
                              title="Marcar como leída"
                            >
                              <i className="bi bi-check2"></i>
                            </button>
                          )}
                        </div>
                      </li>
                    ))
                  ) : (
                    <li className="list-group-item text-center text-muted">Sin notificaciones</li>
                  )}
                </ul>
              </div>
            )}

            <div className="position-relative">
              <button
                type="button"
                className="dashboard-user border-0 bg-transparent p-0"
                onClick={() => setMenuOpen((prev) => !prev)}
              >
                <div className="dashboard-user-meta text-end me-2">
                  <div className="fw-semibold mb-0">
                    {user?.nombre_completo || "Usuario"}
                  </div>
                  <small>{user?.rol?.nombre || "Sin rol"}</small>
                </div>
                <div className="dashboard-user-avatar">
                  {avatarLetter || <i className="bi bi-person"></i>}
                </div>
                <i
                  className={`bi ${
                    menuOpen ? "bi-chevron-up" : "bi-chevron-down"
                  } text-muted ms-2`}
                ></i>
              </button>

              {menuOpen && (
                <div className="dashboard-user-dropdown position-absolute bg-white shadow rounded py-2 end-0 mt-2">
                  <button
                    className="dropdown-item d-flex align-items-center gap-2"
                    onClick={() => {
                      navigate("/dashboard/perfil");
                      setMenuOpen(false);
                    }}
                  >
                    <i className="bi bi-person-circle text-primary"></i>
                    Ver perfil
                  </button>
                  {canOpenConfig && (
                    <button
                      className="dropdown-item d-flex align-items-center gap-2"
                      onClick={() => {
                        navigate("/dashboard/configuracion");
                        setMenuOpen(false);
                      }}
                    >
                      <i className="bi bi-gear text-secondary"></i>
                      Configuracion
                    </button>
                  )}
                  {/* <div className="dropdown-divider"></div> */}
                  <button
                    className="dropdown-item text-danger d-flex align-items-center gap-2"
                    onClick={handleLogout}
                  >
                    <i className="bi bi-box-arrow-right"></i>
                    Cerrar sesion
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="dashboard-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
